/**
 * API Client for Client Management
 *
 * Handles communication with the management backend API.
 * Provides functions for CRUD operations on clients and sync job management.
 */

import type {
  Client,
  ClientFormData,
  PendingClassification,
  ResolveClassificationData,
  DocumentsResponse,
} from '../types/management';
import type {
  SyncsResponse,
  StartSyncResponse,
  SyncSource,
} from '../types/sync';
import {
  createTimeoutSignal,
  getBaseUrl,
  handleHttpError,
  handleNetworkError,
} from './api-utils';

/**
 * Fetch all clients
 *
 * @param authHeader - Authorization header object from auth context
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to array of Client objects
 */
export async function fetchClients(
  authHeader: Record<string, string>,
  signal?: AbortSignal,
): Promise<Client[]> {
  const endpoint = `${getBaseUrl()}/management/clients`;

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      signal: createTimeoutSignal(signal),
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Create a new client
 *
 * @param data - Client form data
 * @param authHeader - Authorization header object from auth context
 * @returns Promise resolving to the created Client
 */
export async function createClient(
  data: ClientFormData,
  authHeader: Record<string, string>,
): Promise<Client> {
  const endpoint = `${getBaseUrl()}/management/clients`;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      body: JSON.stringify(data),
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Update an existing client
 *
 * @param id - Client ID
 * @param data - Client form data
 * @param authHeader - Authorization header object from auth context
 * @returns Promise resolving to the updated Client
 */
export async function updateClient(
  id: string,
  data: ClientFormData,
  authHeader: Record<string, string>,
): Promise<Client> {
  const endpoint = `${getBaseUrl()}/management/clients/${id}`;

  try {
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      body: JSON.stringify(data),
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Delete a client
 *
 * @param id - Client ID
 * @param authHeader - Authorization header object from auth context
 * @returns Promise resolving when deletion is complete
 */
export async function deleteClient(
  id: string,
  authHeader: Record<string, string>,
): Promise<void> {
  const endpoint = `${getBaseUrl()}/management/clients/${id}`;

  try {
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
    });

    await handleHttpError(response, true);
  } catch (error) {
    handleNetworkError(error);
  }
}

// =============================================================================
// Pending Classifications API
// =============================================================================

/**
 * Fetch all pending classifications
 *
 * @param authHeader - Authorization header object from auth context
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to array of PendingClassification objects
 */
export async function fetchPendingClassifications(
  authHeader: Record<string, string>,
  signal?: AbortSignal,
): Promise<PendingClassification[]> {
  const endpoint = `${getBaseUrl()}/management/pending`;

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      signal: createTimeoutSignal(signal),
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Resolve a pending classification (assign to a client)
 *
 * @param id - Pending classification ID
 * @param data - Resolution data containing clientId
 * @param authHeader - Authorization header object from auth context
 * @returns Promise resolving to the resolution result
 */
export async function resolvePendingClassification(
  id: string,
  data: ResolveClassificationData,
  authHeader: Record<string, string>,
): Promise<{
  id: string;
  status: string;
  resolvedClientId: string;
  resolvedClientName: string;
}> {
  const endpoint = `${getBaseUrl()}/management/pending/${id}`;

  try {
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      body: JSON.stringify(data),
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Dismiss a pending classification
 *
 * @param id - Pending classification ID
 * @param authHeader - Authorization header object from auth context
 * @returns Promise resolving when dismissal is complete
 */
export async function dismissPendingClassification(
  id: string,
  authHeader: Record<string, string>,
): Promise<void> {
  const endpoint = `${getBaseUrl()}/management/pending/${id}`;

  try {
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
    });

    await handleHttpError(response, true);
  } catch (error) {
    handleNetworkError(error);
  }
}

// =============================================================================
// Sync Jobs API
// =============================================================================

/**
 * Fetch status of all sync sources
 *
 * @param authHeader - Authorization header object from auth context
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to SyncsResponse with status of all sources
 */
export async function fetchSyncStatus(
  authHeader: Record<string, string>,
  signal?: AbortSignal,
): Promise<SyncsResponse> {
  const endpoint = `${getBaseUrl()}/management/syncs`;

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      signal: createTimeoutSignal(signal),
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Start a sync for the specified source
 *
 * @param source - The sync source to start ('fathom' or 'helpscout')
 * @param authHeader - Authorization header object from auth context
 * @returns Promise resolving to StartSyncResponse
 * @throws Error with message 'Sync already in progress' if sync is already running (409)
 */
export async function startSync(
  source: SyncSource,
  authHeader: Record<string, string>,
): Promise<StartSyncResponse> {
  const endpoint = `${getBaseUrl()}/management/syncs/${source}/start`;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}

// =============================================================================
// Documents API
// =============================================================================

/**
 * Fetch documents for a specific client
 *
 * @param clientFilter - Client slug to filter by (or 'pending' for pending classifications)
 * @param authHeader - Authorization header object from auth context
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Promise resolving to DocumentsResponse with array of Document objects
 */
export async function fetchDocuments(
  clientFilter: string,
  authHeader: Record<string, string>,
  signal?: AbortSignal,
): Promise<DocumentsResponse> {
  const endpoint = `${getBaseUrl()}/management/documents/${encodeURIComponent(clientFilter)}`;

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      signal: createTimeoutSignal(signal),
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}

/**
 * Unassign a document from a client, moving it to pending classification
 *
 * @param s3Key - S3 key of the document to unassign
 * @param authHeader - Authorization header object from auth context
 * @returns Promise resolving to the new pending classification
 */
export async function unassignDocument(
  s3Key: string,
  authHeader: Record<string, string>,
): Promise<{ id: string; status: string }> {
  const endpoint = `${getBaseUrl()}/management/documents/unassign`;

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
      },
      body: JSON.stringify({ s3Key }),
      signal: createTimeoutSignal(),
    });

    await handleHttpError(response, true);
    return response.json();
  } catch (error) {
    handleNetworkError(error);
  }
}
